import type { ReactNode } from 'react'
import React, { createContext, useCallback, useContext, useMemo } from 'react'

import { useModalContext } from '@fourthwall/components'

import type { LaunchStrategyData, LaunchStrategyProgress } from '../LaunchStrategy.types'
import { useContinueBarVisibility } from '../hooks/useContinueBarVisibility'
import { useLaunchStrategyData } from '../hooks/useLaunchStrategyData'
import { useLaunchStrategyProgress } from '../hooks/useLaunchStrategyProgress'
import { useLaunchStrategyVisibility } from '../hooks/useLaunchStrategyVisibility'
import { LaunchStrategyModal } from '../modules/LaunchStrategyModal'
import { getAbsoluteGuideIndex } from '../utils/getAbsoluteGuideIndex'

interface LaunchStrategyContextType {
  data: LaunchStrategyData
  visible: boolean
  showContinueBar: boolean
  progress: LaunchStrategyProgress
  absoluteActiveGuideIndex: number
  totalGuides: number
  setShowContinueBar: (isVisible: boolean) => void
  openModal: () => void
  onDismiss: () => void
}

const LaunchStrategyContext = createContext<LaunchStrategyContextType | undefined>(undefined)

type LaunchStrategyProviderProps = {
  children: ReactNode
}

export const LaunchStrategyProvider = ({ children }: LaunchStrategyProviderProps) => {
  const { open, close } = useModalContext()
  const data = useLaunchStrategyData()
  const { visible, onDismiss } = useLaunchStrategyVisibility()
  const progress = useLaunchStrategyProgress()
  const { visible: showContinueBar, setVisible: setShowContinueBar } = useContinueBarVisibility()

  const totalGuides = data.modal.categories.reduce(
    (acc, category) => acc + category.guides.length,
    0,
  )

  const absoluteActiveGuideIndex = useMemo(
    () =>
      getAbsoluteGuideIndex(data.modal.categories, progress.category.index, progress.guide.index),
    [progress.category.index, data.modal.categories, progress.guide.index],
  )

  const handleModalClose = useCallback(
    (absoluteActiveGuideIndex: number) => {
      if (absoluteActiveGuideIndex !== totalGuides - 1) {
        setShowContinueBar(true)
      }

      close()
    },
    [totalGuides, close, setShowContinueBar],
  )

  const openModal = useCallback(() => {
    setShowContinueBar(false)
    open(LaunchStrategyModal, {
      data: data.modal,
      initialGuideIndex: progress.guide.index,
      initialCategoryIndex: progress.category.index,
      totalGuides,
      onClose: handleModalClose,
      onProgressChange: (categoryIndex, guideIndex) => {
        progress.category.setIndex(categoryIndex)
        progress.guide.setIndex(guideIndex)
      },
    })
  }, [
    setShowContinueBar,
    open,
    data.modal,
    progress.guide,
    progress.category,
    totalGuides,
    handleModalClose,
  ])

  const value = useMemo(
    () => ({
      data,
      visible,
      showContinueBar,
      progress,
      absoluteActiveGuideIndex,
      totalGuides,
      openModal,
      setShowContinueBar,
      onDismiss,
    }),
    [
      data,
      visible,
      showContinueBar,
      progress,
      absoluteActiveGuideIndex,
      totalGuides,
      openModal,
      setShowContinueBar,
      onDismiss,
    ],
  )

  return <LaunchStrategyContext.Provider value={value}>{children}</LaunchStrategyContext.Provider>
}

export const useLaunchStrategyContext = () => {
  const context = useContext(LaunchStrategyContext)
  if (context === undefined) {
    throw new Error('useLaunchStrategy must be used within a LaunchStrategyProvider')
  }

  return context
}
